import { BILLING_MSG, PLAN_CATALOG, PLAN_ORDER, RECEIVED_READ_MSG, SCHEDULE_MSG, TRIAL_DAYS, fillProduct, planSpec, type PlanList, type Product } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { Email, LegalDoc, type LegalArticle } from "../components/Legal.jsx";
import type { ProductInfo } from "../lib/product.js";
import { useProduct } from "../lib/useProduct.js";

// 現場力 特定商取引法に基づく表記。提供：株式会社アイバックス。初版 2026-09-15、料金・お支払い・解約を書き足した版 2026-09-20（縦串11、docs/26 §6）。
// 2026-09-20 射場さんの決定：初回の請求は無料トライアルが終わった翌日・以後は毎月同じ日に前払い／解約しても支払い済みの期間の終わりまで使える・日割りの返金なし。
// PAY.JP の加盟店審査はこのページの「支払時期」「解約・返金（返金の可否・解約の方法・いつまでに解約すれば次の請求が無いか）」を必ず見る。
// 金額は料金表（packages/contract の PLAN_CATALOG。049 から製品ごと）から。税込は API（GET /plans）が制度マスタの税率で計算したもの。
// 049（営業力）：製品名・お問い合わせ先・料金表（在庫オプションの行）が製品で切り替わる。条文は共通。
// 電話番号は「請求があれば開示」のまま（射場さん：本番申請はサイトが完成してから。そのときに決める）。公開前に弁護士等の確認を。
const yen = (n: number) => n.toLocaleString("ja-JP");

/** 販売価格の行。API が返るまで（または落ちているとき）は税抜だけを出す */
function PriceList({ list, product }: { list: PlanList | undefined; product: Product }) {
  const stock = list?.options.stock ?? (PLAN_CATALOG[product].options.stock ? { tiers: PLAN_CATALOG[product].options.stock.tiers.map((t) => ({ key: t.key, items: t.items, priceExTax: t.price, priceInTax: null })) } : null);
  // 工程管理（#100）・スケジュール管理（営業力、#142 段5）。名前は製品ごと。税込は API の料金表が来てから
  const schedule = PLAN_CATALOG[product].options.schedule ? { priceExTax: PLAN_CATALOG[product].options.schedule.price, priceInTax: list?.options.schedule?.priceInTax ?? null } : null;
  // AI書類読み取り（受け取った書類の2回目。両製品。月額は PLAN_CATALOG の1か所＝改定するときはそこだけ。名前は RECEIVED_READ_MSG.optionName＝R3 で「書類の読み取り」から）
  const docRead = { priceExTax: PLAN_CATALOG[product].options.docRead.price, priceInTax: list?.options.docRead?.priceInTax ?? null };
  return (
    <>
      <p>月額の料金（1契約＝1社あたり）は次のとおりです。初期費用はかかりません。</p>
      <ol>
        {PLAN_ORDER.map((code) => {
          const spec = planSpec(code, product);
          const offer = list?.plans.find((p) => p.code === code);
          return (
            <li key={code}>
              {spec.label}プラン（{spec.seats}人まで）：月額 {yen(spec.priceExTax)}円（税抜）{offer ? `／${yen(offer.priceInTax)}円（税込）` : ""}
            </li>
          );
        })}
      </ol>
      {stock ? (
        <p data-testid="legal-stock-option">
          オプション：{BILLING_MSG.stockOption(stock.tiers)}（いずれも税抜）
          {stock.tiers.every((t) => t.priceInTax !== null) ? `／税込 ${stock.tiers.map((t) => `${yen(t.priceInTax as number)}円`).join("、")}` : ""}
        </p>
      ) : null}
      {schedule ? (
        <p data-testid="legal-schedule-option">
          オプション：{fillProduct(SCHEDULE_MSG.optionName, product)} 月額 {yen(schedule.priceExTax)}円（税抜）{schedule.priceInTax !== null ? `／${yen(schedule.priceInTax)}円（税込）` : ""}
        </p>
      ) : null}
      <p data-testid="legal-doc-read-option">
        オプション：{RECEIVED_READ_MSG.optionName} 月額 {yen(docRead.priceExTax)}円（税抜）{docRead.priceInTax !== null ? `／${yen(docRead.priceInTax)}円（税込）` : ""}
      </p>
      <p>登録から{TRIAL_DAYS}日間は、無料でお試しいただけます（無料トライアル。クレジットカードの登録は要りません）。</p>
      <p>現在は、クレジットカードでのお支払いの受付を始める前のため、すべての機能を無料でご利用いただけます。受付を始める日は、事前に電子メール等でお知らせします。</p>
    </>
  );
}

const articlesOf = (list: PlanList | undefined, P: ProductInfo): LegalArticle[] => [
  { heading: "販売事業者", nodes: [{ p: "株式会社アイバックス" }] },
  { heading: "運営統括責任者", nodes: [{ p: "射場 雅弘" }] },
  { heading: "所在地", nodes: [{ p: "〒530-0015 大阪府大阪市北区中崎西2-2-1" }] },
  {
    heading: "電話番号",
    nodes: [{ p: "お問い合わせはメールにて承っております。電話番号は、ご請求があれば遅滞なく開示いたします。" }],
  },
  {
    heading: "お問い合わせ先",
    nodes: [
      {
        jsx: (
          <>
            <p>
              メール：<Email user={P.support.user} domain={P.support.domain} />
            </p>
            <p>受付時間は平日10:00から17:00までとさせていただきます。</p>
          </>
        ),
      },
    ],
  },
  { heading: "販売価格", nodes: [{ jsx: <PriceList list={list} product={P.product} /> }] },
  {
    heading: "商品代金以外に必要な料金",
    nodes: [{ p: "本サービスの利用に必要なインターネット接続料金及び通信費は、利用者のご負担となります。" }],
  },
  {
    heading: "支払方法",
    nodes: [{ p: "クレジットカード（ご利用いただけるカードの種類は、お支払い方法を登録する画面に表示します）。" }],
  },
  {
    heading: "支払時期",
    nodes: [
      {
        p: `無料トライアル（${TRIAL_DAYS}日間）が終わった日の翌日に、初回の料金（1か月分）をご請求します。無料トライアルが終わったあとにお支払い方法を登録された場合は、登録された日にご請求します。`,
      },
      { p: "2回目以降は、毎月、初回のご請求と同じ日に、次の1か月分の料金を前払いで自動的にご請求します。" },
      { p: "クレジットカードの引き落としの日は、ご利用のカード会社の定めによります。" },
    ],
  },
  {
    heading: "役務の提供時期",
    nodes: [
      { p: "利用登録の完了後、直ちに本サービスをご利用いただけます。有料プランは、お支払い方法の登録後、引き続きご利用いただけます。" },
    ],
  },
  {
    heading: "解約・退会及び返金について",
    nodes: [
      {
        p: "有料プランの解約：本サービスにログインし、画面右上の「アカウント」→「料金プラン」から、いつでも解約の手続きができます。次回のご請求の日の前日までに解約の手続きをされた場合、次回以降の料金はご請求しません。",
      },
      {
        p: "解約されたあとも、お支払い済みの期間が終わるまでは、それまでどおりご利用いただけます。お支払い済みの期間が終わったあとは、作成済みの帳票の閲覧、PDF の出力及びデータの持ち出しのみご利用いただけます（データは削除されません）。",
      },
      {
        p: `返金：期間の途中で解約又は退会された場合を含め、既にお支払いいただいた料金は、法令に定める場合を除き、日割り計算による返金を含めて返金いたしません（${P.name}利用規約第6条第5項）。`,
      },
      {
        p: `退会：本サービスの「設定」→「退会」から、いつでも退会できます。退会の手続きから30日間は元に戻すことができ、30日を過ぎるとデータを削除します（${P.name}利用規約第14条）。`,
      },
    ],
  },
  {
    heading: "動作環境",
    nodes: [
      {
        p: "本サービスは、最新版のウェブブラウザ（Google Chrome、Safari、Microsoft Edge 等）でご利用いただけます。ご利用の環境によっては、一部の機能が正しく動作しない場合があります。",
      },
    ],
  },
];

export function TokushohoPage() {
  const P = useProduct();
  // 税込の金額は API（制度マスタの税率）から。ログインしていなくても読める。製品は Host（開発は x-product）、ログイン中は会社の製品
  const q = useQuery({ queryKey: ["plans", P.product], queryFn: () => api<PlanList>("GET", "/plans"), staleTime: 300_000, retry: 1 });
  return (
    <LegalDoc
      title="特定商取引法に基づく表記"
      articles={articlesOf(q.data, P)}
      updated="制定日：2026年9月15日／最終改定日：2026年10月28日"
    />
  );
}
